class ShareCopy extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.url = this.dataset.shareUrl || window.location.href;
    // article.url is percent-encoded for non-ASCII handles (e.g. Cyrillic);
    // decode so the shown/copied link reads like the address bar (…/blogs/мода).
    try {
      this.url = decodeURIComponent(this.url);
    } catch (error) {
      /* keep the raw url if it can't be decoded */
    }
    this.usePopup = this.hasAttribute('data-use-popup');
    this.modal = this.querySelector('[data-share-modal]');
    this.copyBtn = this.querySelector('[data-share-copy]');
    this.copyLabel = this.copyBtn?.querySelector('[data-share-copy-label]');
    this.feedback = this.querySelector('[data-share-feedback]');
    this.urlDisplay = this.querySelector('[data-share-url-display]');
    if (this.urlDisplay) this.urlDisplay.textContent = this.url;
    this.tooltip = this.querySelector('[data-share-tooltip]');
    this.tooltipLabel = this.dataset.tooltipLabel;
    this.triggers = Array.from(this.querySelectorAll('[data-share-open]'));
    this.lastTrigger = null;
    this.copyDefault = this.copyLabel?.textContent;

    this.handleTrigger = this.handleTrigger.bind(this);
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.onCopy = this.onCopy.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.triggers.forEach((btn) => btn.addEventListener('click', this.handleTrigger));
    this.querySelectorAll('[data-share-close]').forEach((el) => el.addEventListener('click', this.close));
    this.copyBtn?.addEventListener('click', this.onCopy);
  }

  handleTrigger(event) {
    if (this.usePopup) {
      this.open(event);
    } else {
      this.copyAndToast();
    }
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    this.cancel('tooltipFrame');
    this.cancel('resetFrame');
    this.unlockScroll();
    // The modal is portaled to <body> while open (see open()); return it so it
    // isn't orphaned and a later reconnect can still find it.
    if (this.modal && this.modal.parentElement === document.body) {
      this.appendChild(this.modal);
    }
  }

  open(event) {
    if (!this.modal) return;
    this.lastTrigger = event.currentTarget;
    if (this.modal.parentElement !== document.body) {
      document.body.appendChild(this.modal);
    }
    this.modal.hidden = false;
    this.modal.classList.add('is-open');
    this.triggers.forEach((btn) => btn.setAttribute('aria-expanded', 'true'));
    this.lockScroll();
    document.addEventListener('keydown', this.onKeydown);
    this.copyBtn?.focus();
  }

  close() {
    if (!this.modal) return;
    this.modal.classList.remove('is-open');
    this.modal.hidden = true;
    this.triggers.forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
    document.removeEventListener('keydown', this.onKeydown);
    this.unlockScroll();
    this.resetCopy();
    this.lastTrigger?.focus();
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
      return;
    }
    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  trapFocus(event) {
    if (!this.modal) return;
    const focusable = this.modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  lockScroll() {
    if (this._prevOverflow === undefined) {
      this._prevOverflow = document.body.style.overflow;
    }
    document.body.style.overflow = 'hidden';
  }

  unlockScroll() {
    if (this._prevOverflow !== undefined) {
      document.body.style.overflow = this._prevOverflow;
      this._prevOverflow = undefined;
    }
  }

  async copyUrl() {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(this.url);
      } else {
        this.fallbackCopy();
      }
    } catch (error) {
      this.fallbackCopy();
    }
  }

  async onCopy() {
    await this.copyUrl();
    this.showCopied();
  }

  async copyAndToast() {
    await this.copyUrl();
    this.showTooltip();
  }

  showTooltip() {
    if (!this.tooltip) return;
    this.tooltip.textContent = this.tooltipLabel || '';
    this.tooltip.classList.add('is-visible');
    this.schedule('tooltipFrame', 1600, () => this.tooltip.classList.remove('is-visible'));
  }

  schedule(key, ms, fn) {
    this.cancel(key);
    let start;
    const step = (now) => {
      if (start === undefined) start = now;
      if (now - start >= ms) {
        this[key] = null;
        fn();
      } else {
        this[key] = requestAnimationFrame(step);
      }
    };
    this[key] = requestAnimationFrame(step);
  }

  cancel(key) {
    if (this[key]) {
      cancelAnimationFrame(this[key]);
      this[key] = null;
    }
  }

  fallbackCopy() {
    const textarea = document.createElement('textarea');
    textarea.value = this.url;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
    } catch (error) {
      /* no-op */
    }
    document.body.removeChild(textarea);
  }

  showCopied() {
    if (this.feedback && this.copiedText) this.feedback.textContent = this.copiedText;
    if (this.copyLabel && this.copiedText) this.copyLabel.textContent = this.copiedText;
    this.schedule('resetFrame', 1800, () => this.resetCopy());
  }

  resetCopy() {
    if (this.copyLabel && this.copyDefault) this.copyLabel.textContent = this.copyDefault;
    if (this.feedback) this.feedback.textContent = '';
  }

  get copiedText() {
    return this.dataset.copiedLabel;
  }
}

if (!customElements.get('share-copy')) {
  customElements.define('share-copy', ShareCopy);
}
